'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import TrackedRoute from 'AdsUI/lib/TrackedRoute';
import { NavMarker } from 'react-rhumb';
import { getAdAccounts } from 'AdsUI/selectors/AccountsSelectors';
import UIListingPage from 'UIComponents/page/UIListingPage';
import DashboardNavigation from 'AdsUI/components/DashboardNavigation';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import UIErrorMessage from 'UIComponents/error/UIErrorMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import PortalIdParser from 'PortalIdParser';
import UILink from 'UIComponents/link/UILink';
import { getNetworkFromParams } from 'AdsUI/selectors/PropsSelectors';
import { getCampaignRequestStatus } from 'AdsUI/selectors/CampaignReportingSelectors';
import { getQueryStringFromFilters } from 'AdsUI/lib/GlobalFiltersQueryParams.js';
import { fetchCampaignDetails } from 'AdsUI/actions/CampaignReportingActions';
import { removeNotification } from 'ReduxMessenger/actions/NotificationActions';
import NoCampaign from 'AdsUI/components/campaign-details/NoCampaign';
import useFilterUpdate from 'AdsUI/lib/useFilterUpdate';
import { getGlobalFilters } from 'AdsUI/selectors/GlobalFilters';
import { AdAccountErrorStates } from '../../records/ad-accounts/AdAccountRecord';
import { useHistory } from 'react-router-dom';
import { Switch } from 'react-router-dom';
import makeLoadable from 'AdsUI/lib/makeLoadable';
import CampaignDetailsPageContainer from 'AdsUI/containers/campaign-details/CampaignDetailsPageContainer';
import CampaignDetailsAnalyzeContainer from 'AdsUI/containers/campaign-details/CampaignDetailsAnalyzeContainer';
import SequenceCampaignDetailsPageContainer from 'AdsUI/containers/campaign-details/SequenceCampaignDetailsPageContainer';
const CampaignEditorContainerLoader = makeLoadable(() => import('AdsUI/containers/campaign-editor/CampaignEditorContainer'));
const settingsUrl = `/settings/${PortalIdParser.get()}/marketing/ads/adAccounts`;
const campaignsUrl = `/ads/${PortalIdParser.get()}`;

const networkOutage = adAccount => adAccount && adAccount.adAccountFetchError && adAccount.adAccountFetchError.errorType === AdAccountErrorStates.NETWORK_API_CALLS_DISABLED;

const SettingsLink = ({
  children
}) => /*#__PURE__*/_jsx(UILink, {
  href: settingsUrl,
  external: true,
  children: children
});

const CampaignsLink = ({
  children
}) => /*#__PURE__*/_jsx(UILink, {
  href: campaignsUrl,
  children: children
});

const NoAdAccount = ({
  network,
  accountId
}) => /*#__PURE__*/_jsx(UIListingPage, {
  headerComponent: /*#__PURE__*/_jsx(DashboardNavigation, {}),
  children: /*#__PURE__*/_jsx(UIErrorMessage, {
    type: "notFound",
    children: /*#__PURE__*/_jsxs(NavMarker, {
      name: "NO_AD_ACCOUNT_PAGE_LOADED",
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: "campaigns.detailsErrors.noAdAccount",
        options: {
          network: I18n.text(network.toLowerCase()),
          accountId: accountId.toString()
        }
      }), /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "campaigns.detailsErrors.connectAdAccount_jsx",
        elements: {
          SettingsLink
        }
      })]
    })
  })
});

const NetworkOutage = ({
  network
}) => /*#__PURE__*/_jsx(UIListingPage, {
  headerComponent: /*#__PURE__*/_jsx(DashboardNavigation, {}),
  children: /*#__PURE__*/_jsx(UIErrorMessage, {
    illustration: "errors/general",
    illustrationProps: {
      width: 200
    },
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "campaigns.detailsErrors.networkOutage.title"
    }),
    children: /*#__PURE__*/_jsx("p", {
      children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "campaigns.detailsErrors.networkOutage.description_jsx",
        options: {
          network: I18n.text(network.toLowerCase())
        },
        elements: {
          CampaignsLink
        }
      })
    })
  })
});

const CampaignDetailsContainer = ({
  adAccounts,
  match: {
    params: {
      accountId,
      campaignId
    }
  },
  location: {
    pathname
  },
  globalFilters,
  network,
  campaignRequestStatus,
  dispatch
}) => {
  const adAccount = adAccounts.find(account => account.network === network && account.accountId === accountId);
  const history = useHistory();
  useFilterUpdate(globalFilters, useCallback(() => {
    if (window.newrelic) {
      window.newrelic.addPageAction('ads-campaign-cache-event', {
        'ads-cache-hit': campaignRequestStatus.fetched,
        'ads-data-type': 'campaign-details'
      });
    }
  }, [campaignRequestStatus]), true);
  useEffect(() => {
    const {
      timeRange,
      attributionView,
      initialized
    } = globalFilters;

    if (adAccount && initialized) {
      if (!adAccount.accessibleToPrimaryUser || networkOutage(adAccount)) {
        return;
      }

      history.replace({
        pathname: history.location.pathname,
        search: getQueryStringFromFilters(globalFilters)
      });

      if (!campaignRequestStatus.fetched && !campaignRequestStatus.fetching) {
        dispatch(fetchCampaignDetails(network, accountId, campaignId, timeRange, attributionView));
      }
    }
  }, [adAccount, globalFilters, network, accountId, campaignId, dispatch, campaignRequestStatus, pathname, history]);
  useEffect(() => {
    if (campaignRequestStatus.fetchFailed) {
      dispatch(removeNotification('CAMPAIGN_DETAILS_FETCH_FAILED'));
    }
  }, [campaignRequestStatus, dispatch]);

  if (networkOutage(adAccount)) {
    return /*#__PURE__*/_jsx(NetworkOutage, {
      network: network,
      accountId: accountId
    });
  }

  if (!adAccount || !adAccount.accessibleToPrimaryUser) {
    return /*#__PURE__*/_jsx(NoAdAccount, {
      network: network,
      accountId: accountId
    });
  }

  if (campaignRequestStatus.fetchFailed) {
    return /*#__PURE__*/_jsx(NoCampaign, {
      network: network,
      campaignId: campaignId
    });
  }

  if (!globalFilters.initialized) {
    return null;
  }

  return /*#__PURE__*/_jsxs(Switch, {
    children: [/*#__PURE__*/_jsx(TrackedRoute, {
      path: "/accounts/:network/:accountId/campaigns/:campaignId/edit",
      component: CampaignEditorContainerLoader,
      name: "campaign-enhanced-editor",
      exact: true
    }), /*#__PURE__*/_jsx(TrackedRoute, {
      path: "/accounts/:network/:accountId/campaigns/:campaignId/adgroups/:adGroupId/edit",
      component: CampaignEditorContainerLoader,
      name: "adgroup-enhanced-editor",
      exact: true
    }), /*#__PURE__*/_jsx(TrackedRoute, {
      path: "/accounts/:network/:accountId/campaigns/:campaignId/edit/:currentTab",
      component: CampaignEditorContainerLoader,
      name: "campaign-enhanced-editor",
      exact: true
    }), /*#__PURE__*/_jsx(TrackedRoute, {
      path: "/accounts/:network/:accountId/campaigns/:campaignId/adgroups/:adGroupId/edit/:currentTab",
      component: CampaignEditorContainerLoader,
      name: "adgroup-enhanced-editor",
      exact: true
    }), /*#__PURE__*/_jsx(TrackedRoute, {
      path: "/accounts/:network/:accountId/campaigns/:campaignId/adgroups/:adGroupId",
      component: CampaignDetailsPageContainer,
      name: "campaign-details"
    }), /*#__PURE__*/_jsx(TrackedRoute, {
      path: "/accounts/:network/:accountId/campaigns/:campaignId/adgroups/:adGroupId/ad/:adId",
      component: CampaignDetailsPageContainer,
      name: "campaign-details"
    }), /*#__PURE__*/_jsx(TrackedRoute, {
      path: "/accounts/:network/:accountId/campaigns/:campaignId/adgroups/:adGroupId/negative-keywords",
      component: CampaignDetailsPageContainer,
      name: "campaign-details-negative-keywords"
    }), /*#__PURE__*/_jsx(TrackedRoute, {
      path: "/accounts/:network/:accountId/campaigns/:campaignId/adgroups/:adGroupId/keywords",
      component: CampaignDetailsPageContainer,
      name: "campaign-details"
    }), /*#__PURE__*/_jsx(TrackedRoute, {
      path: "/accounts/:network/:accountId/campaigns/:campaignId/analyze",
      name: "campaign-details-analyze",
      component: CampaignDetailsAnalyzeContainer,
      exact: true
    }), /*#__PURE__*/_jsx(TrackedRoute, {
      path: "/accounts/:network/:accountId/campaigns/:campaignId",
      component: CampaignDetailsPageContainer,
      name: "campaign-details"
    }), /*#__PURE__*/_jsx(TrackedRoute, {
      path: "/sequences/:sequenceId/accounts/:network/:accountId/campaigns/:campaignId/edit",
      component: CampaignEditorContainerLoader,
      name: "campaign-enhanced-editor",
      exact: true
    }), /*#__PURE__*/_jsx(TrackedRoute, {
      path: "/sequences/:sequenceId/accounts/:network/:accountId/campaigns/:campaignId/adgroups/:adGroupId/edit",
      component: CampaignEditorContainerLoader,
      name: "adgroup-enhanced-editor",
      exact: true
    }), /*#__PURE__*/_jsx(TrackedRoute, {
      path: "/sequences/:sequenceId/accounts/:network/:accountId/campaigns/:campaignId/edit/:currentTab",
      component: CampaignEditorContainerLoader,
      name: "campaign-enhanced-editor",
      exact: true
    }), /*#__PURE__*/_jsx(TrackedRoute, {
      path: "/sequences/:sequenceId/accounts/:network/:accountId/campaigns/:campaignId/adgroups/:adGroupId/edit/:currentTab",
      component: CampaignEditorContainerLoader,
      name: "adgroup-enhanced-editor",
      exact: true
    }), /*#__PURE__*/_jsx(TrackedRoute, {
      path: "/sequences/:sequenceId/accounts/:network/:accountId/campaigns/:campaignId/adgroups/:adGroupId/ad/:adId",
      component: SequenceCampaignDetailsPageContainer,
      name: "campaign-details"
    }), /*#__PURE__*/_jsx(TrackedRoute, {
      path: "/sequences/:sequenceId/accounts/:network/:accountId/campaigns/:campaignId/adgroups/:adGroupId/negative-keywords",
      component: SequenceCampaignDetailsPageContainer,
      name: "campaign-details-negative-keywords",
      exact: true
    }), /*#__PURE__*/_jsx(TrackedRoute, {
      path: "/sequences/:sequenceId/accounts/:network/:accountId/campaigns/:campaignId/adgroups/:adGroupId/keywords",
      component: SequenceCampaignDetailsPageContainer,
      name: "campaign-details",
      exact: true
    }), /*#__PURE__*/_jsx(TrackedRoute, {
      path: "/sequences/:sequenceId/accounts/:network/:accountId/campaigns/:campaignId/adgroups/:adGroupId",
      component: SequenceCampaignDetailsPageContainer,
      name: "campaign-details"
    }), /*#__PURE__*/_jsx(TrackedRoute, {
      path: "/sequences/:sequenceId/accounts/:network/:accountId/campaigns/:campaignId",
      component: SequenceCampaignDetailsPageContainer,
      name: "campaign-details"
    }), /*#__PURE__*/_jsx(TrackedRoute, {
      path: "/sequences/:sequenceId/accounts/:network/:accountId/campaigns/:campaignId/analyze",
      name: "campaign-details-analyze",
      component: CampaignDetailsAnalyzeContainer,
      exact: true
    })]
  });
};

export default connect((state, props) => ({
  adAccounts: getAdAccounts(state),
  network: getNetworkFromParams(state, props),
  globalFilters: getGlobalFilters(state),
  campaignRequestStatus: getCampaignRequestStatus(state, props)
}))(CampaignDetailsContainer);